import { createContext, use } from 'react';

import type { Identity } from './claims';

export type AuthState = {
  isAuthenticated: boolean;
  /** `null` when signed out. Derived from the id token's claims, never fetched. */
  identity: Identity | null;
  /**
   * The vendor every vendor-scoped request is made against. `null` when signed out, or when the user
   * belongs to no approved vendor yet — which is what triggers the select-vendor gate.
   */
  currentVendor: string | null;
  setCurrentVendor: (vendorId: string) => void;
  signOut: () => Promise<void>;
};

// Context + hooks live in this `.ts` file and the provider component in its own `.tsx`: a module
// exporting both a component and non-component values loses fast refresh, and oxlint errors on it
// (`react/only-export-components`). Mirrors `packages/api-client-react`.
export const AuthContext = createContext<AuthState | null>(null);

/** Throws when used outside `AuthProvider`, so a mis-mounted tree fails loudly instead of rendering signed-out. */
export const useAuth = (): AuthState => {
  const value = use(AuthContext);
  if (value === null) throw new Error('useAuth must be used within an AuthProvider.');
  return value;
};

/**
 * The current vendor, asserted non-null. For use **inside the vendor route group only**, where the
 * layout's gate has already turned away anyone without one.
 *
 * A null here is therefore a routing bug, not a user state, and should surface as an error rather than
 * silently issuing an unscoped request against `/vendors/undefined/...`.
 */
export const useCurrentVendor = (): string => {
  const { currentVendor } = useAuth();
  if (currentVendor === null) {
    throw new Error('useCurrentVendor was called outside a vendor-scoped route.');
  }
  return currentVendor;
};
